import { Routes } from '@angular/router';

import { authGuard } from '@/core/guards/auth-guard';
import { accessGuard, dashboardGuard } from '@/core/guards/role.guard';

import { AppLayout } from '@/layout/component/app.layout';
import { Dashboard } from '@/pages/dashboard/dashboard';

import { Documentation } from '@/pages/documentation/documentation';
import { Landing } from '@/pages/landing/landing';
import { Notfound } from '@/pages/notfound/notfound';

// سه بخش سامانه: پنل مدیریت (مدیر = همه‌ی دسترسی‌ها)، پنل پرسنل (دسترسی‌های تعیین‌شده)، و صفحه‌ی رزرو مشتری (/book)
export const appRoutes: Routes = [
  {
    path: '',
    component: AppLayout,
    canActivate: [authGuard],
    children: [
      { path: '', component: Dashboard, canActivate: [dashboardGuard] },

      {
        path: 'profile',
        loadComponent: () => import('./app/project/profile/profile').then((m) => m.ProfileComponent),
        canActivate: [accessGuard()],
      },
      {
        // لاگ سامانه: همه‌ی رویدادها با فیلتر (+ گزارش ورود/خروج کاربران)
        path: 'logs',
        loadComponent: () => import('./app/project/system-log/system-log').then((m) => m.SystemLogComponent),
        canActivate: [accessGuard('logs.view')],
      },
      {
        path: 'userLog',
        loadComponent: () => import('./app/project/user-auth-logs/user-auth-logs').then((m) => m.UserAuthLogsComponent),
        canActivate: [accessGuard('admin')],
      },
      {
        path: 'expiring',
        loadComponent: () =>
          import('./app/project/expiring-enrollments/expiring-enrollments').then((m) => m.ExpiringEnrollmentsPage),
        canActivate: [accessGuard('admin')],
      },
      {
        path: 'users',
        loadComponent: () => import('./app/project/users/users').then((m) => m.UsersComponent),
        canActivate: [accessGuard('admin')],
      },
      {
        path: 'customers',
        loadComponent: () => import('./app/project/customers/customers').then((m) => m.CustomersComponent),
        canActivate: [accessGuard('customers.view')],
      },
      {
        path: 'rooms',
        loadComponent: () => import('./app/project/rooms/rooms').then((m) => m.RoomsComponent),
        canActivate: [accessGuard('rooms.manage')],
      },
      {
        path: 'reservations/new',
        loadComponent: () =>
          import('./app/project/reservations/new-reservation').then((m) => m.NewReservationComponent),
        canActivate: [accessGuard('reservations.create')],
      },
      {
        path: 'reservations',
        loadComponent: () =>
          import('./app/project/reservations/reservations-list').then((m) => m.ReservationsListComponent),
        canActivate: [accessGuard('reservations.view')],
      },
      {
        // درخواست‌های «رزرو با تایید»
        path: 'booking-requests',
        loadComponent: () => import('./app/project/booking-requests/booking-requests').then((m) => m.BookingRequestsComponent),
        canActivate: [accessGuard('reservations.view')],
      },
      {
        path: 'room-availability',
        loadComponent: () =>
          import('./app/project/reservations/room-availability').then((m) => m.RoomAvailabilityComponent),
        canActivate: [accessGuard('reservations.view')],
      },
      {
        path: 'amenities',
        loadComponent: () => import('./app/project/amenities/amenities').then((m) => m.AmenitiesComponent),
        canActivate: [accessGuard('rooms.manage')],
      },
      {
        path: 'time-slots',
        loadComponent: () => import('./app/project/time-slots/time-slots').then((m) => m.TimeSlotsComponent),
        canActivate: [accessGuard('rooms.manage')],
      },
      {
        path: 'booking-blocks',
        loadComponent: () =>
          import('./app/project/booking-blocks/booking-blocks').then((m) => m.BookingBlocksComponent),
        canActivate: [accessGuard('blocks.manage')],
      },
      {
        path: 'pricing',
        loadComponent: () => import('./app/project/pricing/pricing').then((m) => m.PricingComponent),
        canActivate: [accessGuard('pricing.manage')],
      },
      {
        path: 'settings',
        loadComponent: () => import('./app/project/settings/settings').then((m) => m.SettingsComponent),
        canActivate: [accessGuard('settings.manage')],
      },
      {
        // هشدارهای صوتی و نمایشی - جدا از تنظیمات رزرو؛ برای همه‌ی کاربران پنل
        path: 'alerts',
        loadComponent: () => import('./app/project/alerts/alerts').then((m) => m.AlertsComponent),
        canActivate: [accessGuard()],
      },
      {
        path: 'sms',
        loadComponent: () => import('./app/project/sms/sms-management').then((m) => m.SmsManagementComponent),
        canActivate: [accessGuard('sms.view', 'sms.settings')],
      },
      {
        path: 'payments-management',
        loadComponent: () =>
          import('./app/project/payments-management/payments-management').then((m) => m.PaymentsManagementComponent),
        canActivate: [accessGuard('payments.report', 'payments.settings')],
      },
      {
        path: 'printers',
        loadComponent: () => import('./app/project/printers/printers-management').then((m) => m.PrintersManagementComponent),
        canActivate: [accessGuard('printers.manage', 'printers.print')],
      },
      {
        // روی هر رایانه‌ای که QZ Tray و کلید سیستمی دارد باز نگه داشته می‌شود
        path: 'print-station',
        loadComponent: () => import('./app/project/printers/print-station').then((m) => m.PrintStationComponent),
        canActivate: [accessGuard('printers.print')],
      },
      // صفحه‌ی قبلی «اتصال‌ها» به مدیریت پیامک و مدیریت پرداخت تقسیم شد
      { path: 'integrations', redirectTo: 'payments-management' },
      {
        path: 'payment-links',
        loadComponent: () => import('./app/project/payment-links/payment-links').then((m) => m.PaymentLinksComponent),
        canActivate: [accessGuard('payments.manage', 'payments.links')],
      },
      {
        path: 'events',
        loadComponent: () => import('./app/project/events/events').then((m) => m.EventsComponent),
        canActivate: [accessGuard('events.manage')],
      },
      {
        path: 'waitlist',
        loadComponent: () => import('./app/project/waitlist/waitlist').then((m) => m.WaitlistComponent),
        canActivate: [accessGuard('queue.manage')],
      },
      {
        path: 'shifts',
        loadComponent: () => import('./app/project/shifts/shifts').then((m) => m.ShiftsComponent),
        canActivate: [accessGuard('shifts.manage')],
      },
      {
        path: 'surveys',
        loadComponent: () => import('./app/project/surveys/surveys').then((m) => m.SurveysComponent),
        canActivate: [accessGuard('surveys.view')],
      },
      {
        path: 'appearance',
        loadComponent: () => import('./app/project/appearance/appearance').then((m) => m.AppearanceComponent),
        canActivate: [accessGuard('appearance.manage')],
      },
      {
        path: 'help',
        loadComponent: () => import('./app/project/help/help').then((m) => m.HelpComponent),
        canActivate: [accessGuard()],
      },

      // اختیاری: فقط admin
      {
        path: 'uikit',
        loadChildren: () => import('./app/pages/uikit/uikit.routes'),
        canActivate: [accessGuard('admin')],
      },
      { path: 'documentation', component: Documentation, canActivate: [accessGuard('admin')] },
      {
        path: 'pages',
        loadChildren: () => import('./app/pages/pages.routes'),
        canActivate: [accessGuard('admin')],
      },
    ],
  },

  // عمومی
  { path: 'landing', component: Landing },
  { path: 'notfound', component: Notfound },
  { path: 'auth', loadChildren: () => import('./app/pages/auth/auth.routes') },
  {
    // Link2pay: صفحه‌ی فاکتور و پرداخت لینک ارسال‌شده برای مشتری
    path: 'pay/:token',
    loadComponent: () => import('./app/project/public-booking/pay-link').then((m) => m.PayLinkComponent),
  },
  {
    // بخش مجزای مشتریان: حساب کاربری (پروفایل، فاکتورها، کیف پول، اعتبار، بدهی، تراکنش‌ها)
    path: 'account',
    loadComponent: () => import('./app/project/public-booking/customer-account').then((m) => m.CustomerAccountComponent),
  },
  {
    // ثبت‌نام رویداد (دسترسی موقت)
    path: 'event/:slug',
    loadComponent: () => import('./app/project/public-booking/event-page').then((m) => m.EventPageComponent),
  },
  {
    // صف انتظار: ثبت نوبت و پیگیری
    path: 'queue',
    loadComponent: () => import('./app/project/public-booking/queue-page').then((m) => m.QueuePageComponent),
  },
  {
    path: 'queue/:token',
    loadComponent: () => import('./app/project/public-booking/queue-page').then((m) => m.QueuePageComponent),
  },
  {
    path: 'survey/:token',
    loadComponent: () => import('./app/project/public-booking/survey-page').then((m) => m.SurveyPageComponent),
  },
  {
    path: 'book/result',
    loadComponent: () => import('./app/project/public-booking/booking-result').then((m) => m.BookingResultComponent),
  },
  {
    path: 'book',
    loadComponent: () => import('./app/project/public-booking/public-booking').then((m) => m.PublicBookingComponent),
  },

  // 404
  { path: '**', redirectTo: '/notfound' },
];
